/**
 * ============================================================
 * KEJJA OS — src/pages/calendar/AgendaView.tsx
 * Scrollable list of upcoming events grouped by day.
 * ============================================================
 */

import { useMemo } from 'react';
import { Repeat, Bell, MapPin, CalendarDays } from 'lucide-react';
import { useCalendarContext } from './CalendarLayout';
import { useEvents } from '@/hooks/useCalendar';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

export default function AgendaView() {
  const {
    homeId, cursorDate, visibleCalendarIds, routinesOn, openNewEvent,
  } = useCalendarContext();

  // Show 30 days forward from cursor
  const { fromStr, toStr } = useMemo(() => {
    const from = new Date(cursorDate);
    from.setUTCHours(0, 0, 0, 0);
    const to = new Date(from);
    to.setUTCDate(from.getUTCDate() + 30);
    return {
      fromStr: from.toISOString().slice(0, 10),
      toStr: to.toISOString().slice(0, 10),
    };
  }, [cursorDate]);

  const { data: eventsData, isLoading } = useEvents(
    homeId,
    visibleCalendarIds.size > 0
      ? { from: fromStr, to: toStr, calendarIds: Array.from(visibleCalendarIds), includeRoutines: routinesOn }
      : null,
  );

  // Group by date
  const grouped = useMemo(() => {
    const map = new Map<string, {
      events: NonNullable<typeof eventsData>['events'];
      routines: NonNullable<typeof eventsData>['routines'];
    }>();
    for (const e of eventsData?.events ?? []) {
      const bucket = map.get(e.occurrenceDate) ?? { events: [], routines: [] };
      bucket.events.push(e);
      map.set(e.occurrenceDate, bucket);
    }
    for (const r of eventsData?.routines ?? []) {
      const bucket = map.get(r.occurrenceDate) ?? { events: [], routines: [] };
      bucket.routines.push(r);
      map.set(r.occurrenceDate, bucket);
    }
    return Array.from(map.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [eventsData]);

  const todayStr = new Date().toISOString().slice(0, 10);

  if (isLoading) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-20 rounded-xl" />
        <Skeleton className="h-20 rounded-xl" />
        <Skeleton className="h-20 rounded-xl" />
      </div>
    );
  }

  if (grouped.length === 0) {
    return (
      <Card className="border-dashed">
        <CardContent className="p-8 text-center">
          <CalendarDays className="w-10 h-10 text-muted-foreground/40 mx-auto mb-3" />
          <p className="text-sm text-muted-foreground">
            Nothing in the next 30 days. Tap{' '}
            <button
              onClick={() => openNewEvent()}
              className="text-indigo-600 dark:text-indigo-400 hover:underline font-medium"
            >
              New
            </button>
            {' '}to add an event.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4 pb-8">
      {grouped.map(([date, { events, routines }]) => {
        const d = new Date(date + 'T00:00:00Z');
        const isToday = date === todayStr;
        const isTomorrow = date === new Date(Date.now() + 86_400_000).toISOString().slice(0, 10);

        return (
          <section key={date}>
            <div className="flex items-baseline gap-2 mb-2">
              <div className={cn(
                'text-sm font-bold',
                isToday && 'text-indigo-600 dark:text-indigo-400',
              )}>
                {isToday ? 'Today' : isTomorrow ? 'Tomorrow' : d.toLocaleDateString('en-KE', { weekday: 'long' })}
              </div>
              <div className="text-xs text-muted-foreground">
                {d.toLocaleDateString('en-KE', { month: 'short', day: 'numeric' })}
              </div>
            </div>

            <Card>
              <CardContent className="p-0 divide-y divide-border">
                {events.map((e) => {
                  const color = e.color ?? e.calendarColor;
                  return (
                    <div key={e.id + e.occurrenceDate} className="p-3 flex items-start gap-3">
                      <div
                        className="w-1 self-stretch rounded-full flex-shrink-0"
                        style={{ backgroundColor: color }}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start gap-2">
                          {e.isReminder && <Bell className="w-3 h-3 mt-1 text-amber-600 flex-shrink-0" />}
                          <div className="font-medium text-sm truncate flex-1">{e.title}</div>
                        </div>
                        <div className="text-[11px] text-muted-foreground mt-0.5 flex items-center gap-2 flex-wrap">
                          {e.allDay ? (
                            <span>All day</span>
                          ) : (
                            <span className="tabular-nums">
                              {new Date(e.startsAt).toISOString().slice(11, 16)}
                              {e.endsAt && ` – ${new Date(e.endsAt).toISOString().slice(11, 16)}`}
                            </span>
                          )}
                          <span style={{ color }}>· {e.calendarName}</span>
                          {e.recurrence !== 'none' && (
                            <span className="flex items-center gap-0.5">
                              <Repeat className="w-2.5 h-2.5" />{e.recurrence}
                            </span>
                          )}
                        </div>
                        {e.location && (
                          <div className="text-[11px] text-muted-foreground mt-0.5 flex items-center gap-1">
                            <MapPin className="w-2.5 h-2.5" />{e.location}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
                {routines.map((r) => (
                  <div key={r.id + r.occurrenceDate} className="p-3 flex items-start gap-3 bg-teal-500/5">
                    <div className="w-1 self-stretch rounded-full flex-shrink-0 bg-teal-500" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <Repeat className="w-3 h-3 text-teal-700 dark:text-teal-400 flex-shrink-0" />
                        {r.icon && <span className="text-sm">{r.icon}</span>}
                        <div className="font-medium text-sm truncate">{r.title}</div>
                      </div>
                      <div className="text-[11px] text-muted-foreground mt-0.5">
                        <span className="text-teal-700 dark:text-teal-400">Routine</span>
                        {' · '}{r.cadence}
                        {r.ownerUserId === null && ' · shared'}
                      </div>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          </section>
        );
      })}
    </div>
  );
}
